<!--
 * @Author: caiyuliu
 * @Date: 2025-12-15 09:46:13
 * @LastEditors: caiyuliu
 * @LastEditTime: 2025-12-15 10:04:22
 * @Description: 产品卡片组件
-->
<template>
  <div class="product-card" :style="{ borderLeftColor: product.color }">
    <div class="card-header" @click="toggleExpand">
      <div class="product-info">
        <span class="product-icon">{{ product.icon }}</span>
        <div class="product-text">
          <h3>{{ product.name }}</h3>
          <p class="product-name-en">{{ product.nameEn }}</p>
        </div>
      </div>
      <div class="card-actions">
        <span class="protocol-count">{{ product.protocols.length }} 个协议</span>
        <span class="expand-icon" :class="{ expanded: isExpanded }">▼</span>
      </div>
    </div>

    <transition name="slide">
      <div v-if="isExpanded" class="card-body">
        <div v-for="protocol in product.protocols" :key="protocol.name" class="protocol-item" @click="navigateToProtocol(protocol)">
          <div class="protocol-info">
            <span class="protocol-type-badge" :style="{ backgroundColor: getTypeBadgeColor(protocol.type) }">
              {{ protocol.typeName }}
            </span>
            <span class="protocol-name">{{ protocol.comment || protocol.name }}</span>
          </div>
          <div class="protocol-meta">
            <span v-if="protocol.version !== '1.0'" class="protocol-version">v{{ protocol.version }}</span>
            <span v-if="protocol.language.code !== 'zh-CN'" class="protocol-language">{{ protocol.language.name }}</span>
            <span class="protocol-arrow">→</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ProductCard',
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isExpanded: false
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    navigateToProtocol(protocol) {
      this.$router.push(protocol.path)
    },
    getTypeBadgeColor(type) {
      const colorMap = {
        disclaimer: '#FF9800',
        privacy: '#2196F3',
        userAgreement: '#4CAF50',
        organize: '#9C27B0',
        install: '#607D8B',
        other: '#757575'
      }
      return colorMap[type] || '#757575'
    }
  }
}
</script>

<style lang="scss" scoped>
.product-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .card-header {
    padding: 20px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;

    &:hover {
      background: #f5f5f5;
    }

    .product-info {
      display: flex;
      align-items: center;
      gap: 16px;

      .product-icon {
        font-size: 40px;
        line-height: 1;
      }

      .product-text {
        h3 {
          margin: 0 0 4px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        .product-name-en {
          margin: 0;
          font-size: 12px;
          color: #999;
        }
      }
    }

    .card-actions {
      display: flex;
      align-items: center;
      gap: 12px;

      .protocol-count {
        font-size: 14px;
        color: #666;
        background: #f0f0f0;
        padding: 4px 12px;
        border-radius: 12px;
      }

      .expand-icon {
        font-size: 12px;
        color: #999;
        transition: transform 0.3s ease;

        &.expanded {
          transform: rotate(180deg);
        }
      }
    }
  }

  .card-body {
    border-top: 1px solid #eee;
    padding: 12px 20px 20px;

    .protocol-item {
      padding: 12px;
      margin-bottom: 8px;
      border-radius: 8px;
      background: #fafafa;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &:last-child {
        margin-bottom: 0;
      }

      &:hover {
        background: #f0f0f0;
        transform: translateX(4px);

        .protocol-arrow {
          transform: translateX(4px);
        }
      }

      .protocol-info {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;

        .protocol-type-badge {
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          color: #fff;
          font-weight: 500;
          white-space: nowrap;
        }

        .protocol-name {
          font-size: 14px;
          color: #333;
        }
      }

      .protocol-meta {
        display: flex;
        align-items: center;
        gap: 8px;

        .protocol-version,
        .protocol-language {
          font-size: 12px;
          color: #666;
          background: #fff;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .protocol-arrow {
          font-size: 14px;
          color: #999;
          transition: transform 0.2s ease;
        }
      }
    }
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

@media (max-width: 768px) {
  .product-card {
    .card-header {
      padding: 16px;

      .product-info {
        gap: 12px;

        .product-icon {
          font-size: 32px;
        }

        .product-text h3 {
          font-size: 16px;
        }
      }

      .card-actions {
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
      }
    }

    .card-body {
      padding: 12px 16px 16px;

      .protocol-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;

        .protocol-meta {
          width: 100%;
          justify-content: flex-end;
        }
      }
    }
  }
}
</style>
